import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-gas-consumption-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './gas-consumption-chart.component.html',
  styleUrl: './gas-consumption-chart.component.css'
})
export class GasConsumptionChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  labels: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  costsActualLabel: string = "Costs (Actual)";
  costsForecastLabel: string = "Costs (Forecast)";
  smcActualLabel: string = "Average Price per smc ( Actual )";
  smcForecastLabel: string = "Average Price per smc ( Forecast )";
  consumptionActualLabel: string = "Consumption (Actual)";
  consumptionForecastLabel: string = "Consumption (Forecast)";

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyconsumption: (number | null)[] = [20, 30, 25, 35, 40, 28, 33, 45, 37, null, null, null];
  dummyconsumptionForecasted: (number | null)[] = [null, null, null, null, null, null, null, null, null, 42, 31, 39];
  dummycostsPerSmc: (number | null)[] = [5.85, 9.49, 7.15, 9.36, 11.7, 8.58, 6.93, 6.11, 7.02, 5.59, 6.5, 6.76];
  dummytotalCost: (number | null)[] = [117.0, 284.7, 178.15, 374.0, 308.0, 405.0, 259.0, 294.0, 320.0, 260.0, 230.0, 240.0];

  @Input() consumption: (number | null)[] = [20, 30, 25, 35, 40, 28, 33, 45, 37, null, null, null];
  @Input() consumptionForecasted: (number | null)[] = [null, null, null, null, null, null, null, null, null, 42, 31, 39];

  @Input() costsPerSmc: (number | null)[] = [5.85, 9.49, 7.15, 9.36, 11.7, 8.58, 6.93, 6.11, 7.02, 5.59, 6.5, 6.76];
  @Input() costsPerSmcForecastedStartMonth: number | null = null;

  @Input() totalCost: (number | null)[] = [117.0, 284.7, 178.15, 374.0, 308.0, 405.0, 259.0, 294.0, 320.0, 260.0, 230.0, 240.0];
  @Input() totalCostForecastedStartMonth: number | null = null;

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('dashboard.costs_actual_label'),
      this.translocoService.selectTranslate('dashboard.costs_forecast_label'),
      this.translocoService.selectTranslate('dashboard.average_price_per_smc_actual_label'),
      this.translocoService.selectTranslate('dashboard.average_price_per_smc_forecast_label'),
      this.translocoService.selectTranslate('dashboard.consumption'),
      this.translocoService.selectTranslate('dashboard.consumption_forecast'),
    ]).subscribe((
      [
        costsActualTranslation,
        costsForecastTranslation,
        smcActualTranslation,
        smcForecastTranslation,
        consumptionTranslationAC,
        consumptionTranslationFC
      ]
    ) => {
      this.costsActualLabel = costsActualTranslation;
      this.costsForecastLabel = costsForecastTranslation;
      this.smcActualLabel = smcActualTranslation;
      this.smcForecastLabel = smcForecastTranslation;
      this.consumptionActualLabel = consumptionTranslationAC;
      this.consumptionForecastLabel = consumptionTranslationFC;
    });

    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.labels = translations;
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.consumption = this.dummyconsumption;
      this.consumptionForecasted = this.dummyconsumptionForecasted;
      this.costsPerSmc = this.dummycostsPerSmc;
      this.costsPerSmcForecastedStartMonth = 5;
      this.totalCost = this.dummytotalCost;
      this.totalCostForecastedStartMonth = 5;
    }

    Chart.register(ChartDataLabels);

    this.data = {
      labels: this.labels,
      datasets: [
        {
          type: 'bar',
          label: this.consumptionActualLabel,
          backgroundColor: '#7EA4A4',
          data: this.consumption,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            color: '#000000',
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            textAlign: 'center',
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0') || value} \nsmc` },
            clamp: true,
            align: 'top',
            anchor: 'auto',
          },
        },
        {
          type: 'bar',
          label: this.consumptionForecastLabel,
          backgroundColor: '#C0D3D2',
          data: this.consumptionForecasted,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            color: '#000000',
            font: {
              size: 10,
              weight: 'normal',
              family: 'Gilroy',
            },
            textAlign: 'center',
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0') || value} \nsmc` },
            align: 'top',
            anchor: 'auto',
            clip: false,
          },
        }
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            generateLabels: (chart: any) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset: any, index: any) => {
                return {
                  text: dataset.label,
                  fillStyle: dataset.type === 'bar' ? dataset.backgroundColor : dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  pointStyle: dataset.type === 'line' ? (dataset.borderDash ? 'dash' : 'line') : 'circle',
                  lineDash: dataset.type === 'line' && dataset.borderDash ? [2, 2, 2] : [],
                  lineWidth: dataset.type === 'line' ? 4 : 0,

                  hidden: !chart.isDatasetVisible(index),
                  datasetIndex: index,
                };
              });
            },
            font: {
              size: 12,
            },
            color: '#000000',
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              let costLabel = this.costsActualLabel;
              let smcLabel = this.smcActualLabel;

              if (this.costsPerSmcForecastedStartMonth !== null) {
                if (index >= this.costsPerSmcForecastedStartMonth) {
                  costLabel = this.costsForecastLabel;
                  smcLabel = this.smcForecastLabel;
                }
              }

              const pricePerSmc = this.costsPerSmc[index] !== null ? parseFloat(this.costsPerSmc[index]!.toString()).toFixed(2) : "N/A";
              const totalCost = this.totalCost[index] !== null ? parseFloat(this.totalCost[index]!.toString()).toFixed(2) : "N/A";

              var consumptionLabel = this.consumptionActualLabel;

              var totalConsumption = this.consumption[index] !== null ? parseFloat(this.consumption[index]!.toString()).toFixed(2) : "N/A";
              if (this.consumption[index] == null) {
                totalConsumption = this.consumptionForecasted[index] !== null ? parseFloat(this.consumptionForecasted[index]!.toString()).toFixed(2) : "N/A";
                consumptionLabel = this.consumptionForecastLabel;
              }

              return [
                `${smcLabel}: ${this.decimalPipe.transform(pricePerSmc, '1.2-2') || pricePerSmc} EUR/smc`,
                `${costLabel}: ${this.decimalPipe.transform(totalCost, '1.0-0') || totalCost} EUR`,
                `${consumptionLabel}: ${this.decimalPipe.transform(totalConsumption, '1.0-0') || totalConsumption} smc`
              ];
            }
          }
        }

      },
      scales: {
        x: {
          stacked: true,
          title: {
            display: false,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: {
            display: true,
          },
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
        },
        x2: {
          beginAtZero: true,
          display: false,
        },
        y2: {
          beginAtZero: true,
          display: false,
        },
        x3: {
          beginAtZero: true,
          display: false,
        },
        y3: {
          beginAtZero: true,
          display: false,
        }
      },
    };
  }
}
